import {toInterfaceArray} from '../helpers/helpers';
import { Base } from './Base';
import { IUnit, Unit } from './food';
import {Footprint, IFootprint} from './Footprint';
import {SingleCategoryStatistic} from './Statistics';

export class FoodProduct {
    id: string;
    private _name: string;
    get name(): string { return this.displayName(); }
    get nameWithoutBrand(): string { return this._name; }
    brand: string;
    caloriesPerKg: number;
    get caloriesPer100g(): number { return this.caloriesPerKg / 10; }
    footprintPerKg: number;
    get emissionsPer100g(): number { return this.footprintPerKg * 1000 / 10; }
    footprint: Footprint;
    get emissionsPerCalorie(): number | undefined { return this.calculateEmissionsPerCalorie(); }
    carbsPer100g: number;
    carbsKnown = false;
    fatPer100g: number;
    fatKnown = false;
    proteinPer100g: number;
    proteinKnown = false;
    get macrosKnown(): boolean { return this.carbsKnown && this.proteinKnown && this.fatKnown; }
    possibleUnits: Unit[];
    get baseUnit(): string { return this.possibleUnits && this.possibleUnits.length > 0 ? this.possibleUnits[0].baseUnit : 'g'; }
    category: string;
    subCategory: string;
    imgUrl: string;
    hasZeroCalories = false; // Mainly for water products which don't have calories but are valid anyway
    lastChangedAt: Date;

    get categoryStatisticEmissions(): SingleCategoryStatistic { return this.generateCategoryStatistic(this.emissionsPer100g); }
    get categoryStatisticCalories(): SingleCategoryStatistic { return this.generateCategoryStatistic(this.caloriesPer100g); }

    constructor(i?: IFoodProduct) {
        if (i){
            this.id = i.id;
            this._name = i.name;
            this.brand = i.brand;
            this.caloriesPerKg = i.caloriesPerKg;
            this.footprintPerKg = i.footprintPerKg;
            if (i.footprint) { this.footprint = new Footprint(i.footprint); }
            this.carbsPer100g = i.carbsPer100g;
            if (i.carbsKnown) { this.carbsKnown = i.carbsKnown; }
            this.fatPer100g = i.fatPer100g;
            if (i.fatKnown) { this.fatKnown = i.fatKnown; }
            this.proteinPer100g = i.proteinPer100g;
            if (i.proteinKnown) { this.proteinKnown = i.proteinKnown; }
            this.possibleUnits = [];
            if (i.possibleUnits) {
                i.possibleUnits.forEach(possibleUnit => {
                    this.possibleUnits.push(new Unit(possibleUnit));
                });
            }
            this.category = i.category;
            this.subCategory = i.subCategory;
            this.imgUrl = i.imgUrl;
            if (i.hasZeroCalories) { this.hasZeroCalories = i.hasZeroCalories; }
            this.lastChangedAt = new Date(i.lastChangedAt);
        }
        if (!this.possibleUnits) { this.possibleUnits = []; }
    }

    private displayName(): string {
        if (!this.brand || this.brand === '' || !this._name || this._name === '') { return this._name; }
        return this._name + ' - ' + this.brand;
    }

    private calculateEmissionsPerCalorie(): number | undefined {
        if (this.hasZeroCalories) { return Number.MAX_SAFE_INTEGER; }
        if (!this.caloriesPerKg || !this.footprintPerKg) { return undefined; }
        return this.footprintPerKg / this.caloriesPerKg * 1000;
    }

    private generateCategoryStatistic(value: number): SingleCategoryStatistic {
        if (!!this.subCategory) {
            return new SingleCategoryStatistic(this.category, value, this.subCategory);
        }
        return new SingleCategoryStatistic(this.category, value);
    }

    public setName(name: string): void {
        this._name = name;
    }

    public toInterface(): IFoodProduct {
        // This check is necessary to make sure old data (before makro data was introduced) is not causing trouble
        // in the backend. For some reason, strange dates are submitted which are no valid json
        const lastChangedAt = this.lastChangedAt.getFullYear() > 2000 ? this.lastChangedAt : undefined;
        return {id: this.id, name: this._name, brand: this.brand, caloriesPerKg: this.caloriesPerKg, footprintPerKg: this.footprintPerKg,
            footprint: this.footprint?.toInterface(), carbsPer100g: this.carbsPer100g, carbsKnown: this.carbsKnown,
            fatPer100g: this.fatPer100g, fatKnown: this.fatKnown, proteinPer100g: this.proteinPer100g,
            proteinKnown: this.proteinKnown, possibleUnits: toInterfaceArray(this.possibleUnits), category: this.category,
            subCategory: this.subCategory, imgUrl: this.imgUrl, hasZeroCalories: this.hasZeroCalories,
            lastChangedAt};
    }
}

export interface IFoodProduct {
    id: string;
    name: string;
    brand: string;
    caloriesPerKg: number;
    footprintPerKg: number;
    footprint: IFootprint;
    carbsPer100g: number;
    carbsKnown: boolean;
    fatPer100g: number;
    fatKnown: boolean;
    proteinPer100g: number;
    proteinKnown: boolean;
    possibleUnits: IUnit[];
    category: string;
    subCategory: string;
    imgUrl: string;
    hasZeroCalories: boolean;
    lastChangedAt: Date;
}

export function isIFoodProduct(object: any): object is IFoodProduct {
    return 'brand' in object;
}

export class MealFoodProduct extends Base {
    private get amountInGram(): number { return this.unit.toGram(this.amount); }
    private get amountIn100g(): number { return this.amountInGram / 100; }
    private get amountInKg(): number { return this.amountInGram / 1000; }
    get emissions(): number { return this.amountInKg * this.foodProduct.footprintPerKg * 1000; } // Emissions ins grams
    get calories(): number { return this.amountInKg * this.foodProduct.caloriesPerKg; }
    get carbsKnown(): boolean { return this.foodProduct.carbsKnown; }
    get carbs(): number { return this.carbsKnown ? (this.foodProduct.carbsPer100g * this.amountIn100g) : 0; }
    get fatKnown(): boolean { return this.foodProduct.fatKnown; }
    get fat(): number { return this.fatKnown ? (this.foodProduct.fatPer100g * this.amountIn100g) : 0; }
    get proteinKnown(): boolean { return this.foodProduct.proteinKnown; }
    get protein(): number { return this.proteinKnown ? (this.foodProduct.proteinPer100g * this.amountIn100g) : 0; }
    get macrosKnown(): boolean { return this.carbsKnown && this.proteinKnown && this.fatKnown; }
    get name(): string { return this.foodProduct.name; }
    get imgUrl(): string {return this.foodProduct.imgUrl; }

    get categoryStatisticEmissions(): SingleCategoryStatistic { return this.generateCategoryStatistic(this.emissions); }
    get categoryStatisticCalories(): SingleCategoryStatistic { return this.generateCategoryStatistic(this.calories); }

    constructor(foodProduct: FoodProduct | IMealFoodProduct){
        super();
        if (foodProduct instanceof FoodProduct){
            this._foodProduct = foodProduct;
            this.amount = 0;
            // First unit in possibile units array is the default unit
            if (this.foodProduct.possibleUnits && this.foodProduct.possibleUnits.length > 0) {
                this.unit = this.foodProduct.possibleUnits[0];
            }
        }else{
            this._foodProduct = new FoodProduct(foodProduct.foodProduct);
            this.amount = foodProduct.amount;
            this.unit = new Unit(foodProduct.unit);
        }
    }

    get amountWithUnit(): string {
        if (this.unit.name === 'g' || this.unit.name === 'ml') { return this.amount + this.unit.name; }
        if (this.unit.name === 'Packung' || this.unit.name === 'Portion') { return this.amount + ' ' + this.unit.name; }
        return this.amount + ', ' + this.unit.name;
    }
    amount: number;
    unit: Unit;
    private _foodProduct: FoodProduct;
    get foodProduct(): FoodProduct { return this._foodProduct; }

    static deepCopyArray(mealFoodProducts: MealFoodProduct[]): MealFoodProduct[] {
        const ret: MealFoodProduct[] = [];
        mealFoodProducts.forEach(element => {
            ret.push(element.deepCopy());
        });
        return ret;
    }

    toInterface(): IMealFoodProduct {
        return {amount: this.amount, unit: this.unit.toInterface(), foodProduct: this.foodProduct.toInterface()};
    }

    deepCopy(): MealFoodProduct {
        return new MealFoodProduct(this.toInterface());
    }

    equals(mealFoodProduct: MealFoodProduct): boolean{
        return this.amount === mealFoodProduct.amount && this.unit.equals(mealFoodProduct.unit) &&
            this.foodProduct.id === mealFoodProduct.foodProduct.id;
    }

    equalsDeep(mealFoodProduct: MealFoodProduct): boolean {
        if (!this.equals(mealFoodProduct)) { return false; }
        return this.foodProduct.lastChangedAt.getTime() === mealFoodProduct.foodProduct.lastChangedAt.getTime();
    }

    public updateMacros(carbs: number, fat: number, protein: number): boolean {
        this._foodProduct.carbsPer100g = carbs;
        this._foodProduct.proteinPer100g = protein;
        this._foodProduct.fatPer100g = fat;
        this._foodProduct.carbsKnown = true;
        this._foodProduct.proteinKnown = true;
        this._foodProduct.fatKnown = true;
        return true;
    }

    private generateCategoryStatistic(value: number): SingleCategoryStatistic {
        const ret = this.foodProduct.categoryStatisticEmissions; // as the value will be overwritten, it doesn't matter
        // if emissions or calories
        ret.value = value;
        return ret;
    }
}

export interface IMealFoodProduct {
    amount: number;
    unit: IUnit;
    foodProduct: IFoodProduct;
}

export function isIMealFoodProduct(object: any): object is IMealFoodProduct {
    return 'foodProduct' in object;
}
